import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, AlertTriangle, Copy, Check, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MfaEnrollmentWizard({ challengeToken, onComplete, onCancel }) {
  const [step, setStep] = useState('loading'); // loading | qr | codes | error
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.post(`${API}/api/auth/mfa/enroll/start`, {
          challenge_token: challengeToken,
        });
        setEnroll(data);
        setStep('qr');
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Could not start MFA enrollment');
        setStep('error');
      }
    })();
  }, [challengeToken]);

  const verify = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/mfa/enroll/verify`, {
        challenge_token: challengeToken,
        code: code.trim(),
      });
      setResult(data);
      setStep('codes');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === 'invalid_code') setErr('Invalid code — try a fresh code from your app');
      else if (detail === 'challenge_expired' || detail === 'challenge_invalid') {
        setErr('Enrollment session expired — please sign in again');
        setStep('error');
      } else if (detail === 'challenge_attempts_exhausted') {
        setErr('Too many attempts — please sign in again');
        setStep('error');
      } else setErr(detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    try {
      await axios.post(`${API}/api/auth/mfa/enroll/cancel`, { challenge_token: challengeToken });
    } catch (_e) { /* challenge invalidated server-side regardless */ }
    onCancel();
  };

  const finish = () => {
    localStorage.setItem('apex_token', result.access_token);
    localStorage.setItem('apex_user', JSON.stringify(result.user));
    onComplete();
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(result.backup_codes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (step === 'loading') {
    return <div className="label-xs p-4" data-testid="mfa-enroll-loading">INITIALIZING MFA ENROLLMENT…</div>;
  }

  if (step === 'error') {
    return (
      <div className="space-y-4" data-testid="mfa-enroll-error">
        <div className="flex items-center gap-2 p-2 border" style={{ borderColor: 'var(--accent-red)', color: 'var(--accent-red)', fontSize: 11 }}>
          <AlertTriangle size={14} /> {err}
        </div>
        <button type="button" onClick={onCancel} className="btn-sharp w-full" data-testid="mfa-enroll-back-to-login">
          BACK TO SIGN IN
        </button>
      </div>
    );
  }

  if (step === 'qr') {
    return (
      <form onSubmit={verify} className="space-y-4" data-testid="mfa-enroll-qr-step">
        <div className="flex items-center gap-2 p-2 border" style={{ borderColor: 'var(--accent-amber)', color: 'var(--accent-amber)', fontSize: 11 }} data-testid="mfa-enroll-banner">
          <ShieldCheck size={14} /> MFA ENROLLMENT REQUIRED · SCAN WITH YOUR AUTHENTICATOR APP
        </div>
        <div className="flex justify-center">
          <div style={{ background: '#fff', padding: 8 }}>
            <img
              src={`data:image/png;base64,${enroll.qr_png_b64}`}
              alt="TOTP enrollment QR code"
              style={{ width: 168, height: 168, display: 'block' }}
              data-testid="mfa-enroll-qr-img"
            />
          </div>
        </div>
        <div>
          <div className="label-xs mb-1">MANUAL SETUP KEY (IF YOU CANNOT SCAN)</div>
          <div className="p-2 border font-mono text-[11px] break-all" style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }} data-testid="mfa-enroll-manual-key">
            {enroll.secret}
          </div>
        </div>
        <div>
          <div className="label-xs mb-2">ENTER THE 6-DIGIT CODE FROM YOUR APP</div>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            placeholder="000000"
            className="input-sharp"
            style={{ letterSpacing: '0.35em', textAlign: 'center', fontSize: 18 }}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
            required
            data-testid="mfa-enroll-code-input"
          />
        </div>
        {err && (
          <div className="flex items-center gap-2 p-2 border" style={{ borderColor: 'var(--accent-red)', color: 'var(--accent-red)', fontSize: 11 }} data-testid="mfa-enroll-error-msg">
            <AlertTriangle size={14} /> {err}
          </div>
        )}
        <button type="submit" className="btn-sharp btn-primary w-full" disabled={loading || code.length < 6} data-testid="mfa-enroll-verify-button">
          {loading ? 'VERIFYING…' : 'VERIFY & ENABLE MFA'}
        </button>
        <button
          type="button"
          onClick={cancel}
          className="label-xs underline"
          style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
          data-testid="mfa-enroll-cancel-button"
        >← CANCEL AND RETURN TO SIGN IN</button>
      </form>
    );
  }

  // step === 'codes'
  return (
    <div className="space-y-4" data-testid="mfa-enroll-codes-step">
      <div className="flex items-center gap-2 p-2 border" style={{ borderColor: 'var(--accent-green, #22c55e)', color: 'var(--accent-green, #22c55e)', fontSize: 11 }}>
        <ShieldCheck size={14} /> MFA ENABLED · SAVE YOUR RECOVERY CODES NOW
      </div>
      <div className="label-xs" style={{ color: 'var(--text-secondary)' }}>
        These single-use recovery codes are shown ONCE and cannot be retrieved later.
      </div>
      <div className="p-3 border font-mono text-[12px] grid grid-cols-2 gap-1" style={{ borderColor: 'var(--border)' }} data-testid="mfa-backup-codes-list">
        {result.backup_codes.map((c) => <div key={c}>{c}</div>)}
      </div>
      <button type="button" onClick={copyCodes} className="btn-sharp w-full flex items-center justify-center gap-2" data-testid="mfa-copy-codes-button">
        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'COPIED' : 'COPY ALL CODES'}
      </button>
      <label className="flex items-center gap-2 cursor-pointer" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        <input
          type="checkbox"
          checked={savedConfirmed}
          onChange={(e) => setSavedConfirmed(e.target.checked)}
          data-testid="mfa-codes-saved-checkbox"
        />
        I have saved these recovery codes in a secure place
      </label>
      <button
        type="button"
        onClick={finish}
        className="btn-sharp btn-primary w-full flex items-center justify-center gap-2"
        disabled={!savedConfirmed}
        data-testid="mfa-enroll-continue-button"
      >
        <KeyRound size={12} /> CONTINUE TO DASHBOARD
      </button>
    </div>
  );
}
